import { Dispatch, SetStateAction, useRef, useState } from 'react'
import { useEffect } from 'react'
import { usePrevious } from './usePrevious'

type LoadingProps = {
  initialLoading?: boolean
  initialLoadingOffset?: number
}

const useLoading = ({
  initialLoading = false,
  initialLoadingOffset = 1000
}: LoadingProps = {}): {
  loading: boolean
  setLoading: (loading: boolean, delayed?: boolean) => void
  setLoadingOffset: Dispatch<SetStateAction<number>>
} => {
  const [loadingOffset, setLoadingOffset] =
    useState<number>(initialLoadingOffset)
  const [loadingCount, setLoadingCount] = useState<number>(0)
  const prevLoadingCount = usePrevious(loadingCount)
  const [delayedloading, setDelayedLoading] = useState<boolean>(false)
  const timeoutHandler = useRef<ReturnType<typeof setTimeout>>()

  const clearLaodingTimeout = () => {
    clearTimeout(timeoutHandler.current)
  }

  const handleStart = (offset?: number) => {
    clearLaodingTimeout()
    timeoutHandler.current = setTimeout(() => {
      setDelayedLoading(true)
    }, offset ?? loadingOffset)
  }
  const handleComplete = () => {
    clearLaodingTimeout()
    setDelayedLoading(false)
  }

  const setLoading = (loading: boolean, delayed = true): void => {
    if (loading) {
      if (!delayed) {
        immediatelly.current = true
      }
      setLoadingCount((count) => ++count)
    } else {
      setLoadingCount((count) => (count === 0 ? count : --count))
    }
  }

  useEffect(() => {
    setLoading(initialLoading)
    return clearLaodingTimeout()
  }, [])

  const immediatelly = useRef<boolean>(false)

  useEffect(() => {
    if ((prevLoadingCount ?? 0) === 0 && loadingCount >= 1) {
      handleStart(immediatelly.current ? 0 : null)
    } else if ((prevLoadingCount ?? 0) >= 1 && loadingCount === 0) {
      immediatelly.current = false
      handleComplete()
    }
  }, [loadingCount])

  return { loading: delayedloading, setLoading, setLoadingOffset }
}

export default useLoading
